import { useQuery } from '@tanstack/react-query'
import { Printer } from 'lucide-react'
import { Alert, Button, PageLoader } from '@/components/ui/primitives'
import { useDocumentTitle } from '@/hooks/useDocumentTitle'
import { ApiError, apiGet } from '@/lib/api'
import type { HorariosResponse } from '@/lib/portal-types'
import { BlocoDeHorarios, GradeSemanal } from './components'

/**
 * Pagina de horarios.
 *
 * A pagina mais buscada de um site paroquial, e o motivo declarado do projeto.
 * Duas visoes: a grade semanal fixa (resposta para "que horas e a missa de
 * domingo?") e os proximos dias com as alteracoes ja aplicadas (resposta para
 * "tem missa amanha?").
 */
export function HorariosPage() {
  useDocumentTitle('Horarios de missa')

  const { data, isLoading, error } = useQuery({
    queryKey: ['portal', 'horarios'],
    queryFn: () => apiGet<HorariosResponse>('/api/portal/horarios', { dias: 21 }),
    staleTime: 60 * 1000,
  })

  if (isLoading) return <PageLoader />
  if (error) {
    return (
      <div className="mx-auto max-w-4xl px-4 py-10">
        <Alert tone="danger">
          {error instanceof ApiError ? error.message : 'Nao foi possivel carregar os horarios.'}
        </Alert>
      </div>
    )
  }
  if (!data) return null

  const temAlteracao = data.proximosDias.some(
    (dia) => dia.fechado || dia.slots.some((slot) => slot.changed),
  )

  return (
    <div className="mx-auto max-w-4xl px-4 py-8">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="font-serif text-3xl font-semibold">Horarios de missa</h1>
          <p className="mt-1 text-suave">
            Missas, confissoes e demais celebracoes da paroquia.
          </p>
        </div>
        <Button
          variant="outline"
          className="nao-imprimir"
          onClick={() => window.print()}
        >
          <Printer aria-hidden="true" />
          Imprimir
        </Button>
      </div>

      {temAlteracao && (
        <Alert tone="warning" className="mt-6">
          <strong>Ha alteracoes nos proximos dias.</strong> Confira a lista abaixo antes de sair
          de casa.
        </Alert>
      )}

      <section className="mt-8" aria-labelledby="titulo-proximos">
        <h2 id="titulo-proximos" className="font-serif text-2xl font-semibold">
          Proximos dias
        </h2>
        <p className="mt-1 text-sm text-suave">
          Ja com cancelamentos e mudancas de horario aplicados.
        </p>
        <div className="mt-4 rounded-lg border border-borda bg-cartao p-4">
          <BlocoDeHorarios dias={data.proximosDias} />
        </div>
      </section>

      <section className="mt-10" aria-labelledby="titulo-grade">
        <h2 id="titulo-grade" className="font-serif text-2xl font-semibold">
          Grade semanal
        </h2>
        <p className="mt-1 text-sm text-suave">
          Horarios normais, sem considerar alteracoes pontuais.
        </p>
        <div className="mt-4">
          <GradeSemanal grade={data.grade} />
        </div>
      </section>
    </div>
  )
}
